import { IKey } from '@/common/constants/ikey'
import { resolveRegistryElementXml } from '@/common/utils/resolveRegistryElementXml'
import { pickElementEventScript } from '@/common/utils/scriptNodeText'
import { setType } from '@/common/utils/typedValueSerializer'

export type EditableRegistryElement = {
  formId?: string | number
  panelId?: string | number
  elementId?: string | number
  elementType?: string
  fieldName?: string
  pageDataSlot?: string
  isVisible?: boolean | (() => boolean)
  registeredValue?: { value?: { rows?: unknown[]; empty?: boolean }; rows?: unknown[]; empty?: boolean }
  getValue?: () => { value?: { rows?: unknown[]; empty?: boolean }; rows?: unknown[]; empty?: boolean }
  getLiveFieldValue?: () => unknown
  getContext?: () => { isVisible?: boolean }
  ctx?: { config?: { element?: unknown; columns?: unknown } }
}

function readColumnName(column: unknown): string {
  if (!column || typeof column !== 'object') return ''
  const c = column as Record<string, unknown>
  const name = c.name
  if (typeof name === 'string') return name
  if (name && typeof name === 'object') {
    const node = name as { _text?: string; value?: string }
    return String(node._text ?? node.value ?? '').trim()
  }
  const attrs = c._attributes as Record<string, unknown> | undefined
  return typeof attrs?.name === 'string' ? attrs.name : ''
}

function readColumnType(column: unknown): string {
  if (!column || typeof column !== 'object') return ''
  const c = column as Record<string, unknown>
  const type = c.type
  if (typeof type === 'string') return type.trim()
  if (type && typeof type === 'object') {
    const node = type as { _text?: string; value?: string }
    return String(node._text ?? node.value ?? '').trim()
  }
  return ''
}

function readElementColumns(elementLike: unknown): unknown[] {
  if (!elementLike || typeof elementLike !== 'object') return []
  const columns = (elementLike as { columns?: { column?: unknown } }).columns
  if (!columns) return []
  const raw = columns.column
  return Array.isArray(raw) ? raw : raw ? [raw] : []
}

export function extractEditableColumnNamesFromElement(elementLike: unknown): string[] {
  return readElementColumns(elementLike).map(readColumnName).filter(Boolean)
}

export function extractEditableListboxColumnNamesFromElement(elementLike: unknown): string[] {
  return readElementColumns(elementLike)
    .filter((column) => readColumnType(column).toUpperCase() === 'LISTBOX')
    .map(readColumnName)
    .filter(Boolean)
}

function readRowColumnValue(row: unknown, columnName: string): unknown {
  if (!row || typeof row !== 'object') return undefined
  const r = row as Record<string, unknown>
  const fields = r.fields as Record<string, { value?: unknown }> | undefined
  const cell = fields?.[columnName]
  if (cell && typeof cell === 'object' && 'value' in cell) {
    return cell.value
  }
  return r[columnName]
}

function isEmptyCellValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined
}

/** Строка с частично заполненными колонками — onChange мог не успеть вычислить derived-поля. */
export function editableTableRowNeedsOnChangeRetry(
  row: unknown,
  columnNames: readonly string[],
  listboxColumnNames: readonly string[] = [],
): boolean {
  if (!columnNames.length) return false
  const listbox = new Set(listboxColumnNames)
  let hasEmpty = false
  let hasNonEmpty = false
  let hasNonEmptyNonListbox = false
  for (const name of columnNames) {
    const value = readRowColumnValue(row, name)
    if (isEmptyCellValue(value)) {
      hasEmpty = true
    } else {
      hasNonEmpty = true
      if (!listbox.has(name)) hasNonEmptyNonListbox = true
    }
  }
  if (!hasEmpty || !hasNonEmpty) return false
  // Дефолт каталога listbox без пользовательского текста — не «частичный» onChange.
  if (listbox.size > 0 && !hasNonEmptyNonListbox) return false
  return true
}

export function buildEditableTableOnChangeSelectedData(
  row: unknown,
  rowIndex: number,
  columnName: string,
  tableName: string,
  columnNames: readonly string[],
): Record<string, unknown> {
  const flatRow: Record<string, unknown> = {}
  const fields: Record<string, { value: unknown; type: string }> = {}

  for (const name of columnNames) {
    const value = readRowColumnValue(row, name)
    flatRow[name] = value
    fields[name] = setType(value)
  }

  if (tableName) {
    flatRow[`${tableName}__${IKey.ROW_NUM}`] = rowIndex
    flatRow[`${tableName}__${IKey.COLUMN}`] = columnName
    fields[`${tableName}__${IKey.ROW_NUM}`] = { value: rowIndex, type: 'Long' }
    fields[`${tableName}__${IKey.COLUMN}`] = { value: columnName, type: 'String' }
  }

  flatRow.fields = fields
  return flatRow
}

function resolveTriggerColumn(row: unknown, columnNames: readonly string[]): string {
  let trigger = columnNames[0] ?? ''
  for (const name of columnNames) {
    if (!isEmptyCellValue(readRowColumnValue(row, name))) {
      trigger = name
    }
  }
  return trigger
}

function readTableRowsAndEmpty(entry: unknown): {
  rows: unknown[] | undefined
  empty: boolean | undefined
} {
  if (entry == null || typeof entry !== 'object') {
    return { rows: undefined, empty: undefined }
  }
  const e = entry as {
    type?: string
    value?: { rows?: unknown[]; empty?: boolean }
    rows?: unknown[]
    empty?: boolean
  }
  const inner =
    e.value && typeof e.value === 'object' && !Array.isArray(e.value) ? e.value : e
  const rows = Array.isArray(inner.rows) ? inner.rows : undefined
  const emptyFlag = inner.empty
  return {
    rows,
    empty: typeof emptyFlag === 'boolean' ? emptyFlag : undefined,
  }
}

function isAuthoritativeEmptyTable(entry: unknown): boolean {
  const parsed = readTableRowsAndEmpty(entry)
  if (parsed.empty === true) return true
  return Array.isArray(parsed.rows) && parsed.rows.length === 0 && parsed.empty !== false
}

function readLiveEditableTableEntry(reg: EditableRegistryElement): unknown {
  if (typeof reg.getLiveFieldValue === 'function') {
    const live = reg.getLiveFieldValue()
    if (live !== undefined) return live
  }
  if (reg.registeredValue !== undefined) return reg.registeredValue
  return reg.getValue?.()
}

function isRegistryElementVisible(reg: EditableRegistryElement): boolean {
  if (typeof reg.getContext === 'function') {
    const visible = reg.getContext()?.isVisible
    if (typeof visible === 'boolean') return visible
  }
  if (typeof reg.isVisible === 'function') return reg.isVisible()
  if (typeof reg.isVisible === 'boolean') return reg.isVisible
  return true
}

export function resolveEditableTableRowsFromRegistry(
  reg: EditableRegistryElement,
  getFormField?: (fieldName: string) => unknown,
): unknown[] | undefined {
  const live = readLiveEditableTableEntry(reg)
  const liveParsed = readTableRowsAndEmpty(live)
  if (isAuthoritativeEmptyTable(live)) {
    return liveParsed.rows ?? []
  }
  if (Array.isArray(liveParsed.rows) && liveParsed.rows.length > 0) {
    return liveParsed.rows
  }

  const fieldName = reg.fieldName ?? ''
  const fromForm = fieldName && getFormField ? getFormField(fieldName) : undefined
  const formParsed = readTableRowsAndEmpty(fromForm)
  if (isAuthoritativeEmptyTable(fromForm)) {
    return formParsed.rows ?? []
  }
  if (Array.isArray(formParsed.rows) && formParsed.rows.length > 0) return formParsed.rows

  return liveParsed.rows ?? formParsed.rows
}

export async function retryEditableTableOnChangeIfNeeded(
  elements: Record<string, EditableRegistryElement | null | undefined>,
  formId: string | number | undefined,
  runAction: (ctx: Record<string, unknown>) => Promise<unknown>,
  getFormField?: (fieldName: string) => unknown,
): Promise<void> {
  const wanted =
    formId != null && formId !== '' && formId !== -1 ? String(formId) : null

  for (const reg of Object.values(elements)) {
    if (!reg || String(reg.elementType ?? '').toUpperCase() !== 'EDITABLE') continue
    if (wanted && String(reg.formId ?? '') !== wanted) continue
    if (!isRegistryElementVisible(reg)) continue

    const el = resolveRegistryElementXml(reg)
    if (!el) continue
    if (!pickElementEventScript(el as Record<string, unknown>, 'onChange')?.trim()) continue

    const columnNames = extractEditableColumnNamesFromElement(el)
    if (!columnNames.length) continue
    const listboxColumnNames = extractEditableListboxColumnNamesFromElement(el)

    const rows = resolveEditableTableRowsFromRegistry(reg, getFormField)
    if (!Array.isArray(rows) || rows.length === 0) continue

    const tableName = reg.fieldName ?? ''

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      if (!editableTableRowNeedsOnChangeRetry(row, columnNames, listboxColumnNames)) continue

      const triggerColumn = resolveTriggerColumn(row, columnNames)
      const selectedData = buildEditableTableOnChangeSelectedData(
        row,
        rowIndex,
        triggerColumn,
        tableName,
        columnNames,
      )

      await runAction({
        data: el,
        level: 'element',
        eventName: 'onChange',
        selectedData,
        limitToSelectedData: true,
        formId: reg.formId,
        panelId: reg.panelId,
        elementId: reg.elementId,
        pageSlotId: reg.pageDataSlot,
      })
    }
  }
}
